import { useState } from 'react';
import kit from './kit.module.css';
import styles from './ConversationResetDemo.module.css';

type Stage = 'recap' | 'correcting' | 'continued' | 'fresh';
type Change = 'travelers' | 'dates' | 'budget';

interface Trip {
  dates: string;
  travelers: string;
  budget: string;
}

const SAVED_TRIP: Trip = {
  dates: 'May 12-18',
  travelers: '2 adults',
  budget: 'Under $180 / night',
};

const CHANGES: Record<Change, { button: string; userSays: string; patch: Partial<Trip>; reply: string }> = {
  travelers: {
    button: 'Now 3 adults',
    userSays: "Something changed - it's three adults now.",
    patch: { travelers: '3 adults' },
    reply: 'Got it, three adults. Both shortlisted hotels have triple rooms within budget.',
  },
  dates: {
    button: 'Dates moved',
    userSays: 'We moved the trip to May 19-25.',
    patch: { dates: 'May 19-25' },
    reply: 'Updated to May 19-25. Casa Alfama is still free those nights, but Hotel Miradouro is full. I kept it on the list so you can decide.',
  },
  budget: {
    button: 'Bigger budget',
    userSays: 'We can go up to $220 a night now.',
    patch: { budget: 'Under $220 / night' },
    reply: 'Budget raised to $220. Your two shortlisted hotels stay, and three more now fit if you want options.',
  },
};

export function ConversationResetDemo() {
  const [stage, setStage] = useState<Stage>('recap');
  const [trip, setTrip] = useState<Trip>(SAVED_TRIP);
  const [changed, setChanged] = useState<Change[]>([]);
  const [feedback, setFeedback] = useState(
    'The session expired two days ago. Check the recap - confirm it, correct it, or start fresh.'
  );

  const applyChange = (c: Change) => {
    setTrip((t) => ({ ...t, ...CHANGES[c].patch }));
    setChanged((list) => (list.includes(c) ? list : [...list, c]));
    setStage('correcting');
    setFeedback('The recap updated before the agent did anything. Correct more, or confirm to continue.');
  };

  const confirm = () => {
    setStage('continued');
    setFeedback(
      changed.length
        ? 'The agent continues with your corrected details - nothing was booked on stale context.'
        : 'Confirmed in one click. No dates, budget or destination had to be retyped.'
    );
  };

  const startFresh = () => {
    setStage('fresh');
    setFeedback('The old plan is cleared and nothing carries over. You can still bring it back.');
  };

  const restore = () => {
    setStage('recap');
    setTrip(SAVED_TRIP);
    setChanged([]);
    setFeedback('Plan restored from the saved recap. Check it again before continuing.');
  };

  const isChanged = (c: Change) => changed.includes(c);
  const lastChange = changed[changed.length - 1];

  return (
    <div className={kit.root}>
      <span className={styles.system}>Session restored after 2 days</span>

      <div className={kit.agent}>
        <span className={kit.agentIcon} aria-hidden="true">✧</span>
        <p>Welcome back. Here's where we left your Lisbon trip. Is this still accurate?</p>
      </div>

      {stage !== 'fresh' && (
        <div className={kit.proposal}>
          <div className={kit.proposalTop}>
            <span>Recovered context</span>
            <span>{changed.length ? `${changed.length} edited` : 'Saved 2 days ago'}</span>
          </div>
          <h3 className={kit.proposalTitle}>Lisbon trip</h3>
          <dl className={kit.fields}>
            <dt>Dates</dt>
            <dd className={isChanged('dates') ? styles.changed : undefined}>{trip.dates}</dd>
            <dt>Travelers</dt>
            <dd className={isChanged('travelers') ? styles.changed : undefined}>{trip.travelers}</dd>
            <dt>Hotel</dt>
            <dd className={isChanged('budget') ? styles.changed : undefined}>{trip.budget}</dd>
            <dt>Status</dt>
            <dd>2 hotels shortlisted, not booked</dd>
            <dt>Flights</dt>
            <dd className={styles.missing}>Not discussed yet - I won't guess</dd>
          </dl>

          {(stage === 'recap' || stage === 'correcting') && (
            <>
              <div className={`${kit.row} ${styles.actions}`}>
                <button type="button" className={`${kit.btn} ${kit.btnApprove}`} onClick={confirm}>
                  {stage === 'correcting' ? 'Looks right now' : 'Yes, continue'}
                </button>
                <button type="button" className={`${kit.btn} ${kit.btnGhost}`} onClick={startFresh}>
                  Start fresh
                </button>
              </div>
              <div className={`${kit.row} ${styles.actions}`}>
                <span className={kit.label}>Something changed:</span>
                {(Object.keys(CHANGES) as Change[]).map((c) => (
                  <button
                    key={c}
                    type="button"
                    className={`${kit.btn} ${kit.btnEdit}`}
                    onClick={() => applyChange(c)}
                    disabled={isChanged(c)}
                  >
                    {CHANGES[c].button}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {stage === 'correcting' && lastChange && (
        <>
          <div className={kit.bubbleUser}>{CHANGES[lastChange].userSays}</div>
          <div className={kit.bubbleAgent}>{CHANGES[lastChange].reply}</div>
        </>
      )}

      {stage === 'continued' && (
        <>
          <div className={kit.bubbleUser}>Yes, continue.</div>
          <div className={kit.bubbleAgent}>
            Great. Here are your two shortlisted hotels for {trip.travelers}, {trip.dates}:
          </div>
          <ul className={styles.hotels}>
            <li className={styles.hotel}>
              <span>
                Casa Alfama
                <span className={styles.hotelMeta}>
                  {isChanged('travelers') ? 'Triple room' : 'Double room'} - $164 / night
                </span>
              </span>
              <span className={`${kit.chip} ${kit.chipGood}`}>Available</span>
            </li>
            <li className={styles.hotel}>
              <span>
                Hotel Miradouro
                <span className={styles.hotelMeta}>
                  {isChanged('travelers') ? 'Triple room' : 'Double room'} - $176 / night
                </span>
              </span>
              {isChanged('dates') ? (
                <span className={`${kit.chip} ${kit.chipWarn}`}>Full those dates</span>
              ) : (
                <span className={`${kit.chip} ${kit.chipGood}`}>Available</span>
              )}
            </li>
          </ul>
        </>
      )}

      {stage === 'fresh' && (
        <>
          <div className={kit.bubbleUser}>Start fresh.</div>
          <div className={kit.bubbleAgent}>Done - I've cleared the Lisbon plan. Where would you like to go?</div>
          <div className={kit.row}>
            <button type="button" className={`${kit.btn} ${kit.btnGhost}`} onClick={restore}>
              Bring back the Lisbon plan
            </button>
          </div>
        </>
      )}

      <p className={kit.feedback} aria-live="polite">{feedback}</p>
    </div>
  );
}
